import React, { useRef } from 'react';
import {
  Animated,
  Easing,
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Toast from 'react-native-toast-message';
import { useAppDispatch } from '../store/store';
import { loginWithGoogle } from '../features/auth/authSlice';
import { getGoogleClientId } from '../providers/GoogleAuthProvider';
import { fpColors } from '../styles/theme';

interface GoogleSignInButtonProps {
  /**
   * @deprecated Kept for the native fallback path only.
   * On web the button now self-dispatches `loginWithGoogle`; callers do not
   * need to pass this prop anymore. Left optional so existing callers compile
   * unchanged until mobile gets its own provider integration.
   */
  onPress?: () => void;
  disabled?: boolean;
  label?: string;
}

const GOOGLE_G_URL =
  'https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s48-fcrop64=1,00000000ffffffff-rw';

/**
 * Native placeholder. Mobile Google Sign-In is landing in a follow-up PR that
 * will wire `expo-auth-session/providers/google`; until then we show the same
 * "coming soon" toast the LoginScreen used previously so the UX regression is
 * zero. Keeping the Animated.Pressable look preserves design parity.
 */
const NativeButton: React.FC<GoogleSignInButtonProps> = ({
  onPress,
  disabled = false,
  label = 'Continue with Google',
}) => {
  const scale = useRef(new Animated.Value(1)).current;

  const pressIn = () =>
    Animated.timing(scale, {
      toValue: 0.97,
      duration: 90,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start();
  const pressOut = () =>
    Animated.timing(scale, {
      toValue: 1,
      duration: 140,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();

  const handlePress = () => {
    if (onPress) {
      onPress();
      return;
    }
    Toast.show({
      type: 'success',
      text1: 'Google sign-in on mobile coming soon',
      text2: 'Use email login for now — the mobile flow is shipping next.',
    });
  };

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Pressable
        onPress={handlePress}
        onPressIn={pressIn}
        onPressOut={pressOut}
        disabled={disabled}
        style={[styles.button, disabled && styles.disabled]}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        <Image
          source={{ uri: GOOGLE_G_URL }}
          style={styles.logo}
          accessibilityIgnoresInvertColors
        />
        <Text style={styles.label}>{label}</Text>
      </Pressable>
    </Animated.View>
  );
};

/**
 * Web branch — uses Google Identity Services via `@react-oauth/google`.
 *
 * - `useOneTap` shows the subtle One Tap prompt on mount; if the user is
 *   already signed into Google in the browser they can sign in with a
 *   single click.
 * - `auto_select` silently re-signs-in a returning user that previously
 *   consented — combined with the 7-day JWT expiry, this is what makes
 *   session renewals frictionless.
 * - Dispatch goes through the same Redux thunk as email login so screens
 *   listening to `selectAuthLoading` / `selectAuthError` keep working
 *   without any changes.
 */
/**
 * Inner component that actually uses `@react-oauth/google` hooks.
 *
 * Critically, this is only rendered when we know the client ID exists,
 * which means `GoogleAuthProvider` has mounted `<GoogleOAuthProvider>`
 * above it. Splitting the hook call out of the outer gatekeeper keeps
 * us compliant with the rules of hooks AND prevents the
 * "must be used within GoogleOAuthProvider" crash when the env var is
 * missing in production (e.g. Vercel without the var set).
 */
const WebButtonInner: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  // Lazy-require keeps the native bundler from choking on browser-only code.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { GoogleLogin, useGoogleOneTapLogin } = require('@react-oauth/google');
  const dispatch = useAppDispatch();

  const handleCredential = (credential?: string) => {
    if (!credential) {
      Toast.show({
        type: 'error',
        text1: "Couldn't sign in with Google",
        text2: 'Google did not return a credential. Please try again.',
      });
      return;
    }
    dispatch(loginWithGoogle({ idToken: credential }))
      .unwrap()
      .catch((err: any) => {
        const msg = typeof err === 'string' ? err : err?.message ?? "Couldn't sign in with Google";
        Toast.show({ type: 'error', text1: 'Google sign-in failed', text2: msg });
      });
  };

  // Kick off One Tap / auto-select on mount. Fires its own callback
  // independently of the explicit button below, so both paths converge.
  useGoogleOneTapLogin({
    onSuccess: (resp: { credential?: string }) => handleCredential(resp?.credential),
    onError: () => {
      // Silent — One Tap not showing isn't an error worth toasting.
      if (__DEV__) console.warn('[GoogleSignInButton] One Tap failed or was dismissed.');
    },
    auto_select: true,
    disabled,
  });

  return (
    <View style={styles.webWrap}>
      <GoogleLogin
        onSuccess={(resp: { credential?: string }) => handleCredential(resp?.credential)}
        onError={() =>
          Toast.show({
            type: 'error',
            text1: "Couldn't sign in with Google",
            text2: 'Please try again.',
          })
        }
        theme="outline"
        size="large"
        shape="pill"
        text="continue_with"
        width="100%"
      />
    </View>
  );
};

/**
 * Web gatekeeper. Checks for the Client ID env var BEFORE mounting
 * `WebButtonInner` so that when Vercel/staging/prod are missing the var,
 * we render an inert fallback button instead of crashing the React tree
 * with "must be used within GoogleOAuthProvider" (the provider also
 * early-returns without the var, and the GIS hooks require its context).
 */
const WebButton: React.FC<GoogleSignInButtonProps> = ({ disabled = false }) => {
  // Shared resolver — reads EXPO_PUBLIC_GOOGLE_CLIENT_ID_WEB first, falls
  // back to the public hardcoded constant (see GoogleAuthProvider.tsx for
  // why the fallback exists and why it's safe to commit).
  const clientId = getGoogleClientId();

  if (!clientId) {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn(
        '[GoogleSignInButton] No Google Client ID resolved (env + fallback empty). ' +
          'Rendering disabled fallback button.'
      );
    }
    return <NativeButton disabled />;
  }

  return <WebButtonInner disabled={disabled} />;
};

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = (props) => {
  return Platform.OS === 'web' ? <WebButton {...props} /> : <NativeButton {...props} />;
};

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 12,
    minHeight: 52,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  disabled: {
    opacity: 0.5,
  },
  logo: {
    width: 20,
    height: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: fpColors.onSurface,
    fontFamily: 'Inter_600SemiBold',
  },
  // Wrap the Google-provided iframe so we control width in the flex column.
  webWrap: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
  },
});

export default GoogleSignInButton;
